import { create } from 'zustand';
import { FiltersState } from '../hooks/useFilters';

export interface VacancyPageState {
  contentTab: 'candidates' | 'text' | 'questions';
  activeStatusTab: string;
  candidatesCurrentPage: number;
  scrollPosition: number;
  selectedCandidateIds: string[];
}

export interface SearchPageState {
  filters: Partial<FiltersState>;
  currentPage: number;
  selectedRelevance: string | null;
  scrollPosition: number;
  selectedResumeIds: string[];
  searchResults: any | null;
}

export interface ResponsesPageState {
  responsesSource?: string;
  responsesData: any[];
  responsesItemsByPage?: any[][];
  responsesFound?: number;
  responsesPerPage?: number;
  responsesLoadLimit?: number;
  responsesCurrentPage?: number;
  responsesRelevanceApplied?: boolean;
  responsesRelevancePreset?: 'high' | 'medium' | 'low' | null;
  searchTab: 'search' | 'responses';
  parseMode: 'manual' | 'auto';
  scrollPosition: number;
  selectedResponseIds: string[];
  searchValue: string;
}

interface VacancyStateCacheState {
  vacancyPageStates: Record<number, VacancyPageState>;
  searchPageStates: Record<number, SearchPageState>;
  responsesPageStates: Record<number, ResponsesPageState>;
  
  setVacancyPageState: (vacancyId: number, state: Partial<VacancyPageState>) => void;
  getVacancyPageState: (vacancyId: number) => VacancyPageState | null;
  
  setSearchPageState: (vacancyId: number, state: Partial<SearchPageState>) => void;
  getSearchPageState: (vacancyId: number) => SearchPageState | null;
  
  setResponsesPageState: (vacancyId: number, state: Partial<ResponsesPageState>) => void;
  getResponsesPageState: (vacancyId: number) => ResponsesPageState | null;
  
  clearVacancyState: (vacancyId: number) => void;
  clearAll: () => void;
}

const VACANCY_STATE_CACHE_KEY = 'vacancy_state_cache_v1';
const SEARCH_STATE_CACHE_KEY = 'search_state_cache_v1';
const RESPONSES_STATE_CACHE_KEY = 'responses_state_cache_v1';

const loadFromStorage = <T>(key: string): Record<number, T> => {
  try {
    const saved = sessionStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error(`Error loading ${key} from sessionStorage:`, e);
  }
  return {};
};

const saveToStorage = <T>(key: string, data: Record<number, T>) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error(`Error saving ${key} to sessionStorage:`, e);
  }
};

const defaultVacancyPageState: VacancyPageState = {
  contentTab: 'candidates',
  activeStatusTab: 'all',
  candidatesCurrentPage: 1,
  scrollPosition: 0,
  selectedCandidateIds: [],
};

const defaultSearchPageState: SearchPageState = {
  filters: {},
  currentPage: 1,
  selectedRelevance: null,
  scrollPosition: 0,
  selectedResumeIds: [],
  searchResults: null,
};

const defaultResponsesPageState: ResponsesPageState = {
  responsesSource: 'hh',
  responsesData: [],
  responsesItemsByPage: [],
  responsesFound: 0,
  responsesPerPage: 10,
  responsesLoadLimit: 100,
  responsesCurrentPage: 1,
  responsesRelevanceApplied: false,
  searchTab: 'search',
  parseMode: 'manual',
  scrollPosition: 0,
  selectedResponseIds: [],
  searchValue: '',
};

export const useVacancyStateCache = create<VacancyStateCacheState>((set, get) => ({
  vacancyPageStates: loadFromStorage<VacancyPageState>(VACANCY_STATE_CACHE_KEY),
  searchPageStates: loadFromStorage<SearchPageState>(SEARCH_STATE_CACHE_KEY),
  responsesPageStates: loadFromStorage<ResponsesPageState>(RESPONSES_STATE_CACHE_KEY),

  setVacancyPageState: (vacancyId, state) => {
    const { vacancyPageStates } = get();
    const currentState = vacancyPageStates[vacancyId] || defaultVacancyPageState;
    const newState = { ...currentState, ...state };
    const newStates = { ...vacancyPageStates, [vacancyId]: newState };
    
    saveToStorage(VACANCY_STATE_CACHE_KEY, newStates);
    set({ vacancyPageStates: newStates });
  },

  getVacancyPageState: (vacancyId) => {
    const { vacancyPageStates } = get();
    return vacancyPageStates[vacancyId] || null;
  },

  setSearchPageState: (vacancyId, state) => {
    const { searchPageStates } = get();
    const currentState = searchPageStates[vacancyId] || defaultSearchPageState;
    const newState = { ...currentState, ...state };
    const newStates = { ...searchPageStates, [vacancyId]: newState };
    
    saveToStorage(SEARCH_STATE_CACHE_KEY, newStates);
    set({ searchPageStates: newStates });
  },

  getSearchPageState: (vacancyId) => {
    const { searchPageStates } = get();
    return searchPageStates[vacancyId] || null;
  },

  setResponsesPageState: (vacancyId, state) => {
    const { responsesPageStates } = get();
    const currentState = responsesPageStates[vacancyId] || defaultResponsesPageState;
    const newState = { ...currentState, ...state };
    const newStates = { ...responsesPageStates, [vacancyId]: newState };
    
    saveToStorage(RESPONSES_STATE_CACHE_KEY, newStates);
    set({ responsesPageStates: newStates });
  },

  getResponsesPageState: (vacancyId) => {
    const { responsesPageStates } = get();
    return responsesPageStates[vacancyId] || null;
  },

  clearVacancyState: (vacancyId) => {
    const { vacancyPageStates, searchPageStates, responsesPageStates } = get();
    const newVacancyStates = { ...vacancyPageStates };
    const newSearchStates = { ...searchPageStates };
    const newResponsesStates = { ...responsesPageStates };
    delete newVacancyStates[vacancyId];
    delete newSearchStates[vacancyId];
    delete newResponsesStates[vacancyId];
    
    saveToStorage(VACANCY_STATE_CACHE_KEY, newVacancyStates);
    saveToStorage(SEARCH_STATE_CACHE_KEY, newSearchStates);
    saveToStorage(RESPONSES_STATE_CACHE_KEY, newResponsesStates);
    set({ vacancyPageStates: newVacancyStates, searchPageStates: newSearchStates, responsesPageStates: newResponsesStates });
  },

  clearAll: () => {
    sessionStorage.removeItem(VACANCY_STATE_CACHE_KEY);
    sessionStorage.removeItem(SEARCH_STATE_CACHE_KEY);
    sessionStorage.removeItem(RESPONSES_STATE_CACHE_KEY);
    set({ vacancyPageStates: {}, searchPageStates: {}, responsesPageStates: {} });
  },
}));
